import React, { useEffect } from "react";
import "./style.scss";
import { Link } from "react-router-dom";
import { scrollToTop, satm_hero1, satm_hero2, satm_hero1_mob, satm_logo } from "src/shared/utils/appConstant";
import { pageRoutes } from "src/shared/utils/pageRoutes.constant";
import HomePageFooter from "src/views/home/components/Footer";
import Footer from "src/components/footer";

const SATM: React.FC = () => {

  useEffect(() => {
    scrollToTop();
  }, []);

  const featureCards = [
    {
      num: "1.",
      title: "Centralized Lead Management:",
      desc: "Manage, filter, segment, and update lead information from a single interactive interface — including inline and bulk editing.",
      bullets: [
        "Unified view of all lead details in one place.",
        "Bulk actions and real-time updates keep data consistent.",
      ],
    },
    {
      num: "2.",
      title: "AI-Powered Message Generation:",
      desc: "Generate personalized, context-based messages at scale using lead information, reply status, and reusable templates.",
      bullets: [
        "Bulk generation for multiple leads.",
        "Preview before finalizing and sending.",
      ],
    },
    {
      num: "3.",
      title: "Targeted Email Campaigns:",
      desc: "Create and execute structured campaigns — Pitch, Newsletter, Wishes, and more — with targeted lead selection, scheduling, attachments, and email history tracking.",
      bullets: [
        "Send instantly or schedule for later.",
        "Track full email history and status.",
      ],
    },
    {
      num: "4.",
      title: "Performance Dashboard:",
      desc: "Get real-time visibility into leads, outreach activity, responses, industries, regions, lead status, and management performance through centralized analytics.",
      bullets: [
        "Industry and region-wise performance views.",
        "Outreach effectiveness tracked automatically.",
      ],
    },
    {
      num: "5.",
      title: "Configurable Workflows & Admin Controls :",
      desc: "Give administrators control over regions, industries, campaign types, lead sources, and communication workflows — without depending on development for every change.",
      bullets: [],
    },
    {
      num: "6.",
      title: "Reusable Templates:",
      desc: "Standardize communication with reusable message and email templates organized around follow-up stages and reply statuses.",
      bullets: [],
    },
  ];

  const structuredFlowSteps = [
    {
      num: "01",
      title: "Add & Manage Leads",
      desc: "Leads are added and maintained centrally from a single, organized platform.",
    },
    {
      num: "02",
      title: "Filter & Target",
      desc: "Leads are filtered and grouped for precise outreach targeting.",
    },
    {
      num: "03",
      title: "AI-Powered Messages",
      desc: "AI generates personalized messages from reusable templates at scale.",
    },
    {
      num: "04",
      title: "Execute Campaigns",
      desc: "Campaigns are executed or scheduled for optimal reach and timing.",
    },
    {
      num: "05",
      title: "Real-Time Updates",
      desc: "Responses and campaign status are tracked and updated automatically.",
    },
    {
      num: "06",
      title: "Manage Follow-Ups",
      desc: "Follow-ups are managed systematically to move leads forward in the cycle.",
    },
  ];

  const beforeItems = [
    "Fragmented lead management",
    "Manual, time-consuming communication",
    "Lack of structure in outreach",
    "Limited scalability",
  ];

  const afterItems = [
    "Centralized lead management",
    "Automated, AI-assisted communication",
    "Consistent, structured outreach",
    "Scalable, efficient operations",
  ];

  return (
    <div className="satm-container animate__animated animate__fadeIn">
      {/* 1. Hero Section */}
      <section className="satm-hero">
        <div className="hero-backdrop-pillar" />
        <div className="hero-content-left animate__animated animate__fadeInLeft">
          <h1 className="hero-title">SATM</h1>

          <p className="hero-subtitle">
            SATM is an AI-powered sales automation platform that helps businesses
            manage leads, personalize communication, execute targeted campaigns,
            and track outreach performance — all from one centralized system.
          </p>
        </div>

        {/* Right Side SATM Official Logo */}
        <div className="hero-chevron-right animate__animated animate__fadeInRight">
          <img src={satm_logo} alt="SATM" className="satm-hero-logo" />
        </div>

        {/* Breadcrumb Navigation */}
        <div className="hero-breadcrumb">
          <Link to={pageRoutes.Home} onClick={scrollToTop}>
            Home
          </Link>
          <span className="breadcrumb-sep">/</span>
          <Link to={pageRoutes.Marketplace} onClick={scrollToTop}>
            Marketplace
          </Link>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-active">SATM</span>
        </div>
      </section>

      {/* 2. "What is SATM?" Section */}
      <section className="what-is-satm-section">
        <div className="what-is-container">
          <div className="what-is-text animate__animated animate__fadeInUp">
            <div className="what-is-header-row">
              <div className="title-grey-box" aria-hidden="true" />
              <h2 className="outline-title">
                What is
                <br />
                <span className="satm-outline-row">
                  SATM?
                  <span className="mobile-dots" aria-hidden="true">
                    <span className="dot dot-red" />
                    <span className="dot dot-blue" />
                    <span className="dot dot-red" />
                  </span>
                </span>
              </h2>
            </div>

            {/* In mobile, mockup is displayed between title and description */}
            <div className="what-is-mockup-mobile">
              <div className="satm-layered-mockup">
                <object data={satm_hero1_mob} type="image/svg+xml" aria-label="SATM Dashboard Overview" className="satm-mockup-bg" style={{ pointerEvents: 'none' }} />
              </div>
            </div>

            <p className="what-is-p">
              We built <strong className="highlight-brand">SATM</strong> to transform fragmented sales processes into a centralized and structured workflow — helping teams manage leads, generate personalized communication, run campaigns, and monitor performance efficiently.
            </p>
            <p className="what-is-p">
              SATM is built for sales teams, founders handling outbound, business development teams, and agencies managing outreach at scale.
            </p>
          </div>

          <div className="what-is-mockup animate__animated animate__fadeInRight">
            <div className="satm-layered-mockup">
              <img src={satm_hero1} alt="SATM Dashboard Overview" className="satm-mockup-bg" />
              <img src={satm_hero2} alt="SATM Leads Table" className="satm-mockup-fg" />
            </div>
          </div>
        </div>
      </section>

      {/* 3. "One Platform to Streamline Your Sales Outreach" Section */}
      <section className="streamline-outreach-section">
        <div className="streamline-header">
          <h2 className="streamline-title">
            One Platform to Streamline Your Sales{" "}
            <span className="highlight-blue">Outreach</span>
          </h2>
          <p className="streamline-desc">
            Everything you need to streamline sales outreach, personalize communication, and drive more efficient growth.
          </p>
        </div>

        <div className="streamline-cards-grid">
          {featureCards.map((card, idx) => (
            <div className="streamline-feature-card" key={idx}>
              <div className="card-num">{card.num}</div>
              <h3 className="card-heading">{card.title}</h3>
              <p className="card-body">{card.desc}</p>
              {card.bullets.length > 0 && (
                <ul className="card-bullets">
                  {card.bullets.map((bullet, bIdx) => (
                    <li key={bIdx}>{bullet}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* 4. "From Lead to Response, in One Structured Flow" Section */}
      <section className="structured-flow-section">
        <div className="section-header">
          <h2 className="section-title">
            From Lead to Response, in<br />
            <span className="highlight-blue">One Structured Flow</span>
          </h2>
        </div>

        <div className="flow-interactive-wrapper">
          {/* Left Column: 6 Structured Flow Steps */}
          <div className="flow-steps-list">
            {structuredFlowSteps.map((step, idx) => (
              <div className="flow-step-item" key={idx}>
                <div className="flow-step-header">
                  <span className="flow-step-num">{step.num}</span>
                  <span className="flow-step-title">{step.title}</span>
                </div>
                <p className="flow-step-desc">{step.desc}</p>
              </div>
            ))}
          </div>

          {/* Right Column (Desktop): Converging Flow Lines to SATM Hub */}
          <div className="flow-streams-container" aria-hidden="true">
            <svg
              className="flow-streams-svg"
              viewBox="0 0 540 580"
              preserveAspectRatio="none"
              fill="none"
            >
              {/* 6 Streams aligned with steps 01-06, each containing 6 lines in light colors that merge cleanly on the right */}
              {[
                { y: 35 },
                { y: 137 },
                { y: 239 },
                { y: 341 },
                { y: 443 },
                { y: 545 },
              ].map((stream, sIdx) => {
                const startOffsets = [-5, -3, -1, 1, 3, 5];
                // Light, airy colors from soft sky to medium sky
                const lineColors = [
                  "#bae6fd", // 1. Light pastel sky
                  "#a5f3fc", // 2. Light cyan sky
                  "#7dd3fc", // 3. Soft sky blue
                  "#93c5fd", // 4. Soft pastel blue
                  "#38bdf8", // 5. Clear sky blue
                  "#60a5fa", // 6. Light cornflower blue
                ];

                return (
                  <g key={`stream-${sIdx}`}>
                    {/* Small delicate origin dot aligned with each step pointer */}
                    <circle cx="2" cy={stream.y} r="1" fill="#7dd3fc" />

                    {/* 6 lines per bundle starting a few pixels after the bullet point */}
                    {startOffsets.map((sOff, lIdx) => {
                      const yStart = stream.y + sOff;
                      const strokeColor = lineColors[lIdx];

                      return (
                        <path
                          key={`line-${sIdx}-${lIdx}`}
                          d={`M 7 ${yStart} C 180 ${yStart}, 350 290, 440 290`}
                          stroke={strokeColor}
                          strokeWidth="1.0"
                          strokeDasharray="4 3"
                        />
                      );
                    })}
                  </g>
                );
              })}

              {/* Merged convergence point right in front of SATM */}
              <circle cx="440" cy="290" r="3.5" fill="#38bdf8" />
            </svg>

            <div className="flow-hub-target">
              <span className="hub-label">SATM</span>
            </div>
          </div>
        </div>
      </section>

      {/* 5. "From Tool Sprawl to One-Click Management" Section */}
      <section className="comparison-section">
        <div className="section-header">
          <h2 className="section-title">
            From Tool Sprawl to <span className="highlight-blue">One-Click</span><br />
            <span className="highlight-blue">Management</span>
          </h2>
        </div>

        <div className="comparison-card-wrapper">
          <div className="comparison-table-card">
            {/* Column 1: Before SATM */}
            <div className="comparison-col before-col">
              <div className="col-header before-header">
                <span className="header-title">Before SATM</span>
                <span className="header-badge">Pain points & challenges</span>
              </div>
              <div className="col-rows">
                {beforeItems.map((item, idx) => (
                  <div className="row-item before-item" key={idx}>
                    <span className="item-bullet before-bullet">✕</span>
                    <span className="item-text">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Column 2: After SATM */}
            <div className="comparison-col after-col">
              <div className="col-header after-header">
                <span className="header-title">After SATM</span>
                <span className="header-badge">Solutions & outcomes</span>
              </div>
              <div className="col-rows">
                {afterItems.map((item, idx) => (
                  <div className="row-item after-item" key={idx}>
                    <span className="item-bullet after-bullet">✓</span>
                    <span className="item-text">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Pre-Footer Contact Section */}
      <HomePageFooter page="SATM" />

      {/* 8. Global Footer */}
      <Footer />
    </div>
  );
};

export default SATM;
